import * as THREE from 'three';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type ThreeEvent } from '@react-three/fiber';
import type { PlaneMeshProps } from '../types';
import { ViewerLayer } from './ViewerLayer';
import { useOnClick } from './useOnClick';
import { useAbyssViewerContext } from './AbyssViewerContext';
import { RENDER_ORDER_CUBOID } from './RenderOrder';

export const PlaneMesh = ({
  isVisible,
  position,
  scale,
  rotation,
  color,
  transparent = true,
  opacity = 1,
  forceSinglePass = false,
  depthWrite = true,
  onClick,
  onHover,
  enableClippingByVisibilityBox,
  showInSphericalView,
}: PlaneMeshProps) => {
  const { sharedGeometries, clippingPlanes } = useAbyssViewerContext();
  const { plane: geometry } = sharedGeometries ?? {};

  const meshLayers = useMemo(() => {
    const layers = new THREE.Layers();
    if (isVisible ?? true) {
      layers.enable(ViewerLayer.Default);
      layers.enable(ViewerLayer.AutoPickable);
      if (showInSphericalView) {
        layers.enable(ViewerLayer.SphericalViewerLayer);
      }
    } else {
      layers.disable(ViewerLayer.Default);
      layers.disable(ViewerLayer.AutoPickable);
      layers.disable(ViewerLayer.SphericalViewerLayer);
    }
    return layers;
  }, [isVisible, showInSphericalView]);

  const shouldHandle = useCallback((event: ThreeEvent<MouseEvent>) => {
    const { altKey, ctrlKey, metaKey, shiftKey, button } = event;
    const isLeftButton = button === 0;
    if (!isLeftButton || altKey || ctrlKey || metaKey || shiftKey) {
      return false;
    }
    return true;
  }, []);

  const onMeshClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (onClick) {
        event.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const [isHovered, setIsHovered] = useState<boolean>();

  const onPointerOver = useCallback((event: ThreeEvent<PointerEvent>) => {
    // console.log('onPointerOver', event);
    setIsHovered(true);
    event.stopPropagation();
  }, []);
  const onPointerOut = useCallback((event: ThreeEvent<PointerEvent>) => {
    // console.log('onPointerOut', event);
    setIsHovered(false);
    event.stopPropagation();
  }, []);

  useEffect(() => {
    if (isHovered !== undefined) {
      onHover?.(isHovered);
    }
  }, [onHover, isHovered]);

  const { onPointerDown, onPointerUp } = useOnClick({ onClick: onMeshClick, shouldHandle });

  const optionalClippingPlanes = (enableClippingByVisibilityBox ?? true) ? clippingPlanes : null;

  return (
    <group scale={scale} position={position} rotation={rotation}>
      <mesh
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        geometry={geometry}
        onPointerOver={onPointerOver}
        onPointerOut={onPointerOut}
        layers={meshLayers}
        renderOrder={RENDER_ORDER_CUBOID}
      >
        <meshBasicMaterial
          attach="material"
          clippingPlanes={optionalClippingPlanes}
          color={color}
          transparent={transparent}
          opacity={opacity}
          forceSinglePass={forceSinglePass}
          depthWrite={depthWrite}
          // side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
};
